import React, { useEffect, useState } from "react";
import { Table, Input, DatePicker, Breadcrumb } from "antd";
import { SearchOutlined, CalendarOutlined } from "@ant-design/icons";
import styled from "styled-components";
import COLORS from "common/Colors";
import { useHistory } from "react-router-dom";
import { CALL_API } from "common/API";

const PendingWrap = styled.div`
  .breadcrumb-wrap {
    margin-bottom: 20px;
  }

  .filters-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  .total-count {
    font-weight: 600;
    font-size: 16px;
    color: ${COLORS.TEXT.PRIMARY};
  }

  .filters {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .filter-input.ant-input-affix-wrapper {
    border-radius: 20px !important;
    height: 38px !important;
    display: flex;
    align-items: center;
    padding: 0 14px;
    border: 1px solid #d9d9d9;
    min-width: 180px;
  }

  .filter-input.ant-input-affix-wrapper .ant-input {
    border: none !important;
    box-shadow: none !important;
  }

  .filter-input.ant-input-affix-wrapper:hover,
  .filter-input.ant-input-affix-wrapper:focus-within {
    border-color: ${COLORS.PRIMARY} !important;
    box-shadow: none !important;
  }

  /* Date picker pill style */
  .date-filter.ant-picker {
    border-radius: 20px !important;
    height: 38px !important;
    min-width: 150px;
    border: 1px solid #d9d9d9;
  }

  .date-filter.ant-picker:hover,
  .date-filter.ant-picker-focused {
    border-color: ${COLORS.PRIMARY} !important;
    box-shadow: none !important;
  }

  /* Table styling */
  .ant-table {
    border-radius: 10px;
    border: 1px solid #ececec;
    overflow: hidden;
  }

  .ant-table-thead > tr > th {
    background: #fafafa;
    font-weight: 600;
    font-size: 13px;
    color: #333;
    border-bottom: 1px solid #ececec;
  }

  .ant-table-tbody > tr > td {
    font-size: 13px;
    color: #444;
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #f9fffe !important;
  }
`;

const PendingClient = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [search, setSearch] = useState("");
    const [dateFilter, setDateFilter] = useState(null);
    const history = useHistory();

    useEffect(() => {
        getPendingClients();
    }, []);

    const getPendingClients = async () => {
        try {
            setLoading(true);

            const payload = { statusid: "2" }; // ✅ pending payload

            const response = await CALL_API(
                "/insyts-legal/getalllegalclients",
                "post",
                payload
            );

            if (response?.code === "SUCCESS") {
                const formattedData = response?.data
                ?.map((item, index) => ({
                    key: item?.bdo_id || index,
                    originalData: item,
                    name:
                    item?.clienttype === "person"
                        ? item?.clientname
                        : item?.companyregisteredname,
                    type: item?.clienttype === "company" ? "Company" : "Person",
                    email: item?.officialemail,
                    country: item?.country,
                    submittedOn: item?.createdat
                    ? new Date(item.createdat).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        })
                    : "N/A",
                    rawDate: item?.createdat ? new Date(item.createdat) : null,
                }))
                .sort((a, b) => {
                    // ✅ newest first
                    if (!a.rawDate) return 1;
                    if (!b.rawDate) return -1;
                    return b.rawDate - a.rawDate;
                });

                setData(formattedData || []);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // ── Client-side filtering ──
    const filteredData = data.filter((item) => {
        const matchesSearch =
            !search ||
            item.name?.toLowerCase().includes(search.toLowerCase()) ||
            item.email?.toLowerCase().includes(search.toLowerCase()) ||
            item.country?.toLowerCase().includes(search.toLowerCase());

        const matchesDate =
        !dateFilter ||
        (item.rawDate &&
            item.rawDate.getFullYear() === dateFilter.year() &&
            item.rawDate.getMonth() === dateFilter.month() &&    // ✅ moment month() is 0-indexed, matches JS Date
            item.rawDate.getDate() === dateFilter.date());       // ✅ use .date() not .toDate().toDateString()

        return matchesSearch && matchesDate;
    });

    const columns = [
        {
            title: "Client Name",
            dataIndex: "name",
            render: (text) => <span style={{ fontWeight: 500 }}>{text || "N/A"}</span>,
        },
        {
            title: "Client Type",
            dataIndex: "type",
        },
        {
            title: "Office Email",
            dataIndex: "email",
        },
        {
            title: "Country",
            dataIndex: "country",
        },
        {
            title: "Submitted on",
            dataIndex: "submittedOn",
        },
    ];

    return (
        <PendingWrap>
            {/* Breadcrumb */}
            <div className="breadcrumb-wrap">
                <Breadcrumb>
                    <Breadcrumb.Item
                        onClick={() => history.push("/client")}
                        style={{ cursor: "pointer", color: "#1677ff" }}
                    >
                        Client
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>Pending Client</Breadcrumb.Item>
                </Breadcrumb>
            </div>

            {/* Header row */}
            <div className="filters-header">
                <div className="total-count">
                    List of Pendings: {filteredData.length}
                </div>

                <div className="filters">
                    <Input
                        placeholder="Search"
                        prefix={<SearchOutlined style={{ color: "#aaa" }} />}
                        className="filter-input"
                        allowClear
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    <DatePicker
                        className="date-filter"
                        placeholder="Date"
                        suffixIcon={<CalendarOutlined style={{ color: "#aaa" }} />}
                        onChange={(date) => setDateFilter(date)}
                        allowClear
                    />
                </div>
            </div>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={filteredData}
                loading={loading}
                size="middle"
                pagination={false}
                bordered={false}
                onRow={(record) => ({
                    onClick: () =>
                        history.push({
                            pathname: `/client-new/${record.key}`,
                            state: { editData: record.originalData, editMode: true,isPending: true }, // ← fix keys
                        }),
                    style: { cursor: "pointer" },
                })}
            />
        </PendingWrap>
    );
};

export default PendingClient;